import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/SlotBooking-css/SlotBooking.css";
import logo_0 from '../../assets/images/R2R.png';

export default function SlotBooking() {
  const location = useLocation();
  const navigate = useNavigate();
  const cartItems = location.state?.cartItems || [];

  const [selectedTip, setSelectedTip] = useState(0);
  const [showAddressPopup, setShowAddressPopup] = useState(false);
  const [currentAddress, setCurrentAddress] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [recentLocations, setRecentLocations] = useState([]);

  const [showSlotPopup, setShowSlotPopup] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");

  const itemTotal = cartItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
  const taxFee = 69;
  const totalToPay = itemTotal + taxFee + selectedTip;

  // Add navigation back to services
  const handleBackToServices = () => {
    navigate('/services');
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            const formatted = data.display_name || "Current location";
            setCurrentAddress(formatted);
            updateRecentLocations(formatted);
          } catch (error) {
            console.error("Error fetching address:", error);
            setCurrentAddress("Current location (address not available)");
          }
        },
        (error) => {
          console.error("Error getting location:", error);
          setCurrentAddress("Unable to get current location");
        }
      );
    } else {
      setCurrentAddress("Geolocation is not supported by this browser");
    }
  };

  const updateRecentLocations = (location) => {
    setRecentLocations((prev) => {
      const updated = [location, ...prev.filter((loc) => loc !== location)];
      return updated.slice(0, 5);
    });
  };

  useEffect(() => {
    if (searchQuery.length < 3) {
      setSearchResults([]);
      return;
    }

    const fetchLocations = async () => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${searchQuery}`
        );
        const data = await response.json();
        setSearchResults(data);
      } catch (error) {
        console.error("Error fetching location suggestions:", error);
      }
    };

    fetchLocations();
  }, [searchQuery]);

  const handleAddressSelect = () => {
    if (!currentAddress) return;
    updateRecentLocations(currentAddress);
    setShowAddressPopup(false);
  };

  const handleSelectLocation = (location) => {
    const formatted = location.display_name;
    setCurrentAddress(formatted);
    updateRecentLocations(formatted);
    setShowAddressPopup(false);
  };

  // Check if address is selected and not empty
  const isAddressSelected = currentAddress.trim() !== "";

  return (
    <div className="slot-booking-container">
      <header className="slot-header">
        <img src={logo_0} className="logo" alt="R2R Logo" />
        <h2>Book your Slot</h2>
        {/* Add back button */}
        {/* <button className="back-to-services-btn" onClick={handleBackToServices}>
          ← Back to Services
        </button> */}
      </header>

      <div className="slot-booking-content">
        <div className="left-section">
          <div className="section">
            <span className="icon">📍</span>
            <div>
              <p className="label">Send booking details to</p>
              <p>+91 93xxxx</p>
            </div>
          </div>

          <div className="section">
            <span className="icon">📍</span>
            <div>
              <p className="label">Address</p>
              <button className="address-btn" onClick={() => setShowAddressPopup(true)}>
                {currentAddress || "Select an Address"}
              </button>
            </div>
          </div>

          <div className="section">
            <span className="icon">🕒</span>
            <div>
              <p className="label">Book your Slot</p>
              <button
                onClick={() => setShowSlotPopup(true)}
                disabled={!isAddressSelected}
                style={{
                  cursor: !isAddressSelected ? "not-allowed" : "pointer",
                  opacity: !isAddressSelected ? 0.5 : 1,
                }}
              >
                {selectedDate && selectedTime
                  ? `${selectedDate}, ${selectedTime}`
                  : "Book Slot"}
              </button>
            </div>
          </div>

          <div className="section">
            <span className="icon">💳</span>
            <div className="payment-details">
              <p className="label">Payment Method</p>
              <p>UPI / Card / Netbanking</p>
              <button
                className="change-btn"
                onClick={() =>
                  navigate("/payment", {
                    state: {
                      cartItems,
                      totalToPay,
                    },
                  })
                }
                disabled={!isAddressSelected}
                style={{
                  cursor: !isAddressSelected ? "not-allowed" : "pointer",
                  opacity: !isAddressSelected ? 0.5 : 1,
                }}
              >
                Payment
              </button>
            </div>
          </div>

          <div className="cancel-policy">
            <h4>Cancellation policy</h4>
            <p>
              Free cancellations if done more than 12 hrs before the service or
              if a professional isn't assigned. A fee will be charged otherwise.
            </p>
          </div>
        </div>

        <div className="right-section">
          <div className="service-box">
            <h3 className="service-box-title">Selected Services</h3>
            {cartItems.length > 0 ? (
              cartItems.map((item) => (
                <div key={item.id} className="service-card">
                  <div className="service-card-content">
                    <p className="service-title">{item.name} (Qty: {item.quantity})</p>
                    <span className="service-price">₹{item.price * item.quantity}</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="empty-cart-msg">No services selected</p>
            )}
          </div>

          <div className="payment-summary">
            <h4>Payment summary</h4>
            <div className="summary-line">
              <span>Item total</span>
              <span>₹{itemTotal}</span>
            </div>
            <div className="summary-line">
              <span>Taxes and Fee</span>
              <span>₹{taxFee}</span>
            </div>
            <hr />
            <div className="summary-line total">
              <span>Total amount</span>
              <span>₹{itemTotal + taxFee}</span>
            </div>
            <div className="summary-line">
              <span>Amount to pay</span>
              <span>₹{totalToPay}</span>
            </div>

            <div className="tip-section">
              <p>Add a tip to thank the Professional</p>
              {[10, 20, 30].map((tip) => (
                <button
                  key={tip}
                  className={`tip-btn ${selectedTip === tip ? "selected" : ""}`}
                  onClick={() => setSelectedTip(tip)}
                >
                  ₹{tip}
                </button>
              ))}
            </div>
          </div>

          <div className="amount-box">
            <strong>Amount to pay</strong>
            <span>₹{totalToPay}</span>
          </div>
        </div>
      </div>

      {/* Address Popup */}
      {showAddressPopup && (
        <div className="address-popup-overlay">
          <div className="address-popup-box">
            <input
              type="text"
              className="location-search-input"
              placeholder="Search for your location/society/apartment"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button className="use-current-location" onClick={getCurrentLocation}>
              📍 Use current location
            </button>

            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((result) => (
                  <div
                    key={result.place_id}
                    className="search-result-item"
                    onClick={() => handleSelectLocation(result)}
                  >
                    <p className="location-name">{result.display_name}</p>
                  </div>
                ))}
              </div>
            )}

            {recentLocations.length > 0 && (
              <div className="recent-locations">
                <h4>Recents</h4>
                {recentLocations.map((loc, index) => (
                  <div
                    key={index}
                    className="recent-location-item"
                    onClick={() => {
                      setCurrentAddress(loc);
                      setShowAddressPopup(false);
                    }}
                  >
                    <span className="history-icon">🕘</span>
                    <p>{loc}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="popup-buttons">
              <button
                className="cancel-btn"
                onClick={() => setShowAddressPopup(false)}
              >
                Cancel
              </button>
              <button className="confirm-btn" onClick={handleAddressSelect}>
                Confirm Address
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Slot Booking Popup */}
      {showSlotPopup && (
        <div className="slot-popup-overlay">
          <div className="slot-popup-box">
            <button className="close-btn" onClick={() => setShowSlotPopup(false)}>
              ✕
            </button>
            <h2>When should the professional arrive?</h2>
            <p className="service-duration">Service will take approx. 4 hrs</p>

            <div className="date-grid">
              {["Sun 08", "Mon 09", "Tue 10", "Wed 11", "Thu 12"].map(
                (date, index) => (
                  <button
                    key={index}
                    className={`date-btn ${selectedDate === date ? "selected" : ""}`}
                    onClick={() => setSelectedDate(date)}
                  >
                    {date}
                  </button>
                )
              )}
            </div>

            <div className="payment-note">
              <p>
                <strong>Pay ₹69.6</strong> (refundable)
              </p>
              <p>Balance to be paid - ₹3878.4</p>
            </div>

            <h3>Select start time of service</h3>
            <div className="time-grid">
              {[
                "08:00 AM",
                "08:30 AM",
                "09:00 AM",
                "05:30 PM",
                "06:00 PM",
              ].map((time, index) => (
                <button
                  key={index}
                  className={`time-btn ${selectedTime === time ? "selected" : ""}`}
                  onClick={() => setSelectedTime(time)}
                >
                  {time}
                </button>
              ))}
            </div>

            <button
              className="checkout-btn"
              disabled={!selectedDate || !selectedTime}
              onClick={() => setShowSlotPopup(false)}
            >
              Proceed to checkout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}